import {forwardRef, InputHTMLAttributes, ReactNode, useEffect} from 'react'
import {StyledCheckBox, StyledCheckBoxContainer, StyledCheckBoxInputWrapper, StyledCheckBoxLabel} from './style'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
    id: string
    label?: ReactNode
    labelPosition?: 'left' | 'right'
    name?: string
    errorMessage?: string
    helpText?: string
    placeholder?: string
    containerClass?: string
    invisible?: boolean
    indeterminate?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            id,
            label,
            labelPosition = 'right',
            errorMessage,
            helpText,
            name,
            onChange,
            onBlur,
            containerClass,
            invisible,
            indeterminate,
            ...rest
        },
        ref
    ) => {
        // Handle indeterminate state
        useEffect(() => {
            if (ref && 'current' in ref && ref.current) {
                ref.current.indeterminate = indeterminate || false
            }
        }, [indeterminate, ref])

        // Also handle indeterminate state on ref callback
        const handleRef = (element: HTMLInputElement | null) => {
            if (element) {
                element.indeterminate = indeterminate || false
            }
            if (typeof ref === 'function') {
                ref(element)
            } else if (ref) {
                ref.current = element
            }
        }

        return (
            <StyledCheckBoxContainer $label={label} className={containerClass}>
                <StyledCheckBoxInputWrapper $labelPosition={labelPosition}>
                    <StyledCheckBox
                        id={id}
                        type="checkbox"
                        name={name}
                        onChange={onChange}
                        onBlur={onBlur}
                        ref={handleRef}
                        $invisible={invisible}
                        {...rest}
                    />
                </StyledCheckBoxInputWrapper>
                <Flexbox direction={'column'} gap={2}>
                    <StyledCheckBoxLabel htmlFor={id} $disabled={rest.disabled}>
                        {' '}
                        {label}{' '}
                    </StyledCheckBoxLabel>
                    {(helpText || errorMessage) && <InputHelpText helpText={helpText} error={errorMessage} />}
                </Flexbox>
            </StyledCheckBoxContainer>
        )
    }
)

Checkbox.displayName = 'Checkbox'
